import { useAuthentication } from '@ecosystem/newron-client-lib';
import { feedback } from '@ecosystem/newron-design-system';
import React, { useEffect, useMemo, useReducer, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler/build/OutsideClickHandler';

import './UpsellFeedback.css';
import { Page, UpsellName, UpsellStatus } from '../../common';
import { useUpsellAlert } from '../../hooks/useUpsellAlert';
import { postUpsellAnalytics } from '../../services/Market.service';
import { ClientUserGroup, getUserGroupNamesForUser } from '../../services/Persona.service';
import { UpsellContext, initialState, reducer } from '../../state';
import { getPageBasedOnPath } from '../../utils';
import { Notification } from '../notification';
import { UpsellFeedbackModal } from '../upsell-feedback-modal/UpsellFeedbackModal';

export type MarketPeriod = {
  name: string;
  periodicity: string;
  published: string;
  sequence: number;
  value: string;
};

export type OpenMarketOptions = {
  value: string;
  periods: MarketPeriod[];
  countryName: string;
  productName: string;
};

export type UpsellFeedbackProps = {
  openMarketsOptions: OpenMarketOptions[];
  cells: string[];
};

export const getPageByPath = (): Page => {
  const path = window.location.pathname;
  return getPageBasedOnPath(path);
};

export function UpsellFeedback({ cells, openMarketsOptions }: UpsellFeedbackProps): JSX.Element {
  const [state, dispatch] = useReducer(reducer, initialState);
  const contextValue = useMemo(() => ({ state, dispatch }), [state]);
  const [showModal, setShowModal] = useState(false);
  const { showUpsell, upsellNudgeDataForSlide } = useUpsellAlert(cells, openMarketsOptions);

  const { user, getAccessToken } = useAuthentication();

  const [clientUserGroups, setClientUserGroups] = useState<ClientUserGroup>();

  const { Alert } = feedback;

  const discoverMoreHandler = (value: boolean) => {
    setShowModal(value);
  };

  useEffect(() => {
    (async () => {
      const token = await getAccessToken();
      const fetchedClientData = await getUserGroupNamesForUser(token);
      setClientUserGroups(fetchedClientData);
    })();
  }, [getAccessToken]);

  const postUpsellAnalyticsData = async () => {
    const analyticsData = {
      userId: user?.userId,
      upsellName: UpsellName.MONTHLY_DATA,
      upsellStatus: UpsellStatus.UPSELL_SHOWN,
    };

    const token = await getAccessToken();
    await postUpsellAnalytics(token, analyticsData);
  };

  useEffect(() => {
    if (showUpsell && upsellNudgeDataForSlide) {
      postUpsellAnalyticsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showUpsell, upsellNudgeDataForSlide]);

  const title = (
    <div id="upsellfeedback" data-test-id="upsell-feedback">
      You are not working with the latest available data.{' '}
      <button
        className="text-discover-more"
        type="button"
        data-test-id="upsell-feedback-discover-more"
        onClick={() => discoverMoreHandler(true)}
      >
        Discover more here
      </button>
    </div>
  );

  return (
    <OutsideClickHandler onOutsideClick={() => discoverMoreHandler(false)}>
      <UpsellContext.Provider value={contextValue}>
        {showUpsell && <Alert type={'info'}>{title}</Alert>}
        <UpsellFeedbackModal
          showModal={showModal}
          discoverMoreHandler={discoverMoreHandler}
          upsellNudgeDataForSlide={upsellNudgeDataForSlide}
          clientUserGroupsData={clientUserGroups}
        />
        <Notification />
      </UpsellContext.Provider>
    </OutsideClickHandler>
  );
}
